import React, { useMemo, useState } from "react";
import cn from "classnames";
import { useAtom } from "jotai";
import { useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";
import { getItemGroups, deleteItemGroup } from "features/Products/api";
import { transformItemsGroup } from "features/Products/helpers";
import {
  activeItemsGroupAtom,
  selectedProductAtom,
  ALL_ITEMS_GROUP,
} from "atoms/catalogAtom";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, RegularText } from "components/Text";
import { Button } from "components/Button";
import { Accordion } from "components/Accordion";
import { LoadingView, ErrorView } from "components/Feedback";
import { useModal, ConfirmationModal, InfoModal } from "components/Modal";

import { GroupModal } from "./GroupModal";
import { MoveToGroupModal } from "./MoveToGroupModal";
import styles from "./styles.module.scss";

export const GROUPS_QUERY_KEY = ["item-groups"];

const GroupRow = ({ group, isActive, onClick, onDoubleClick }) => (
  <div
    className={cn(styles.listRow, "hoverBg", isActive && styles.listRowActive)}
    onClick={onClick}
    onDoubleClick={onDoubleClick}
  >
    <RegularText>{group.name}</RegularText>
  </div>
);

export const GroupsPanel = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const [activeGroup, setActiveGroup] = useAtom(activeItemsGroupAtom);
  const [, setSelectedProduct] = useAtom(selectedProductAtom);

  const [expanded, setExpanded] = useState(new Set());

  const groupsQuery = useQuery(GROUPS_QUERY_KEY, () => getItemGroups());
  const groups = groupsQuery.data?.items ?? [];
  const tree = useMemo(() => transformItemsGroup(groups), [groups]);

  const isAllGroup = String(activeGroup?.itemgroupid ?? "0") === "0";
  const noSelection = !activeGroup || isAllGroup;

  const selectGroup = (group) => {
    setActiveGroup(group);
    setSelectedProduct(null);
  };

  const openGroupModal = (isEdit) =>
    openModal(<GroupModal isEdit={isEdit} groups={groups} />);

  const toggleAccordion = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onDeleteClick = () => {
    if (noSelection) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить группу "${activeGroup.name}"?`}
        onConfirm={async () => {
          try {
            await deleteItemGroup(activeGroup.itemgroupid);
            queryClient.invalidateQueries(GROUPS_QUERY_KEY);
            queryClient.invalidateQueries(["products"]);
            selectGroup(ALL_ITEMS_GROUP);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  const onMoveClick = () => {
    if (noSelection) return;
    openModal(<MoveToGroupModal mode="group" groups={groups} />);
  };

  const renderTree = (nodes) =>
    nodes.map((node) => {
      const row = (
        <GroupRow
          key={node.itemgroupid}
          group={node}
          isActive={activeGroup?.itemgroupid === node.itemgroupid}
          onClick={() => {
            if (activeGroup?.itemgroupid !== node.itemgroupid) {
              selectGroup(node);
            }
          }}
          onDoubleClick={() => openGroupModal(true)}
        />
      );

      if (node.children?.length) {
        return (
          <Accordion
            key={node.itemgroupid}
            withIcon
            arrowZone
            collapsed={!expanded.has(node.itemgroupid)}
            handleCollapse={() => toggleAccordion(node.itemgroupid)}
            parent={row}
          >
            <div className={styles.treeChildren}>
              {renderTree(node.children)}
            </div>
          </Accordion>
        );
      }
      return row;
    });

  return (
    <PageLayout
      className={styles.groupsPanel}
      header={<BoldText>Группы товаров</BoldText>}
      footer={
        <ActionsRow className={styles.footerRow}>
          <Button
            icon="plus"
            onClick={() => openGroupModal(false)}
            title="Создать группу"
          />
          <Button icon="move" onClick={onMoveClick} disabled={noSelection}>
            Переместить
          </Button>
          <Button
            icon="settings"
            onClick={() => openGroupModal(true)}
            disabled={noSelection}
          >
            Свойства
          </Button>
          <Button
            icon="delete"
            onClick={onDeleteClick}
            disabled={noSelection}
            title="Удалить группу"
          />
        </ActionsRow>
      }
    >
      {groupsQuery.isLoading ? (
        <LoadingView />
      ) : groupsQuery.isError ? (
        <ErrorView
          message={getErrorMessage(groupsQuery.error)}
          onRetry={groupsQuery.refetch}
        />
      ) : (
        <div className={styles.groupsList}>
          <GroupRow
            group={ALL_ITEMS_GROUP}
            isActive={isAllGroup}
            onClick={() => selectGroup(ALL_ITEMS_GROUP)}
          />
          {renderTree(tree)}
        </div>
      )}
    </PageLayout>
  );
};
